import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiLogOut } from "react-icons/fi";

const LogoutConfirmDialog = ({ open, onClose, onConfirm }) => {
    const cancelRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        cancelRef.current?.focus();
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (typeof document === "undefined") return null;

    return createPortal(
        <AnimatePresence>
            {open && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                    role="presentation"
                >
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] cursor-default"
                        onClick={onClose}
                        aria-hidden
                    />
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="logout-title"
                        aria-describedby="logout-desc"
                        initial={{ opacity: 0, scale: 0.96, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 10 }}
                        transition={{ type: "spring", stiffness: 380, damping: 28 }}
                        className="relative w-full max-w-[400px] rounded-2xl bg-white shadow-2xl shadow-black/15 ring-1 ring-black/5 overflow-hidden"
                    >
                        <div className="p-6 sm:p-8">
                            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-nt-blue/10 text-nt-blue">
                                <FiLogOut size={26} strokeWidth={2} aria-hidden />
                            </div>
                            <h2
                                id="logout-title"
                                className="text-center text-lg font-semibold text-nt-blue sm:text-xl"
                            >
                                Sign out?
                            </h2>
                            <p
                                id="logout-desc"
                                className="mt-2 text-center text-sm text-nt-grey leading-relaxed"
                            >
                                You will need to sign in again to continue.
                            </p>
                            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                <button
                                    ref={cancelRef}
                                    type="button"
                                    onClick={onClose}
                                    className="w-full sm:w-auto min-h-[44px] rounded-xl border-2 border-nt-grey-light px-5 py-2.5 text-sm font-semibold text-nt-blue hover:bg-nt-grey-light/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-nt-blue focus-visible:ring-offset-2 transition-colors sm:min-w-[120px]"
                                >
                                    Stay signed in
                                </button>
                                <button
                                    type="button"
                                    onClick={onConfirm}
                                    className="w-full sm:w-auto min-h-[44px] rounded-xl bg-nt-red px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-nt-red focus-visible:ring-offset-2 transition-opacity sm:min-w-[120px]"
                                >
                                    Sign out
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body,
    );
};

export { LogoutConfirmDialog };
